import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import {
  BaseProps,
  getClassByTextType,
  TextType,
  ViewIntent,
} from '../common/props';
import { useInverseBg } from '../theme/useInverseBg';

export interface ITitleProps extends BaseProps {
  /**
   * Inverzní verze - typicky používáme na tmavším pozadí.
   */
  inverse?: boolean;
  /**
   * Úroveň zanoření nadpisu, 1 - nejvýše, 5 - nejníže.
   * @deprecated nově se používá type.
   */
  level?: number;
  /**
   * Typ velikosti textu - doporučované použití - logický typ nadpisu - level je již deprecated.
   */
  type?: TextType;
  /**
   * Typ informace.
   */
  intent?: ViewIntent;
  /**
   * Kurzíva.
   */
  italic?: boolean;
  /**
   * Podtržený.
   */
  underline?: boolean;
  /**
   * Extra akce, výchozí umístění co nejvíce vpravo na řádce.
   */
  extra?: React.ReactNode;
  /**
   * Akce umístěné hned za textem.
   */
  extraLeft?: boolean;
  icon?: React.ReactNode;
  required?: boolean;
}

export type TitleProps = ITitleProps;

/**
 * Komponenta nadpisu.
 */
export const Title = ({
  className,
  style,
  level,
  intent,
  italic,
  underline,
  children,
  extra,
  extraLeft,
  icon,
  type,
  inverse,
  required,
  ...other
}: TitleProps) => {
  const parentInverse = useInverseBg(inverse);
  let cls1 = classNames(
    Classes.TITLE,
    Classes.TYPOGRAPHY,
    className,
    getClassByTextType(type),
    {
      [Classes.TITLE_H1]: level === 1,
      [Classes.TITLE_H2]: level === 2,
      [Classes.TITLE_H3]: level === 3,
      [Classes.TITLE_H4]: level === 4,
      [Classes.TITLE_H5]: level === 5,
      [Classes.TITLE_EXTRA_LEFT]: extraLeft,
    }
  );
  let cls2 = classNames({
    [Classes.TYPOGRAPHY_INHERIT]: intent === 'inherit',
    [Classes.TYPOGRAPHY_PRIMARY]: intent === 'primary',
    [Classes.TYPOGRAPHY_SECONDARY]: intent === 'secondary',
    [Classes.TYPOGRAPHY_DANGER]: intent === 'danger',
    [Classes.TYPOGRAPHY_SUCCESS]: intent === 'success',
    [Classes.TYPOGRAPHY_WARNING]: intent === 'warning',
    [Classes.TYPOGRAPHY_INACTIVE]: intent === 'inactive',
    [Classes.TYPOGRAPHY_DEFAULT]: !intent || intent === 'default',
    [Classes.TYPOGRAPHY_INVERSE]: parentInverse,
  });
  let contentCls = classNames(Classes.TITLE_CONTENT, {
    [Classes.TYPOGRAPHY_ITALIC]: italic,
    [Classes.TYPOGRAPHY_UNDERLINE]: underline,
    [Classes.RF_REQUIRED]: required,
  });
  let mainCls = extra ? cls1 : classNames(cls1, cls2);

  let content;
  if (extra) {
    content = (
      <div className={Classes.TITLE_WRAPPER}>
        <div className={classNames(Classes.TITLE_TITLE, cls2)}>
          {icon && (
            <div className={Classes.TITLE_ICON}>
              {icon}
              {children && <>&nbsp;</>}
            </div>
          )}
          <div className={contentCls}>{children}</div>
        </div>
        <div className={classNames(Classes.TITLE_EXTRA, {})}>{extra}</div>
      </div>
    );
  } else {
    content = (
      <div className={Classes.TITLE_WRAPPER}>
        <div className={classNames(Classes.TITLE_TITLE, cls2)}>
          {icon && (
            <div className={Classes.TITLE_ICON}>
              {icon}
              {children && <>&nbsp;</>}
            </div>
          )}
          <div className={contentCls}>{children}</div>
        </div>
      </div>
    );
  }

  switch (level) {
    case 2:
      return (
        <h2 {...other} className={mainCls} style={style}>
          {content}
        </h2>
      );
    case 3:
      return (
        <h3 {...other} className={mainCls} style={style}>
          {content}
        </h3>
      );
    case 4:
      return (
        <h4 {...other} className={mainCls} style={style}>
          {content}
        </h4>
      );
    case 5:
      return (
        <h5 {...other} className={mainCls} style={style}>
          {content}
        </h5>
      );
    case 1:
    default:
      return (
        <h1 {...other} className={mainCls} style={style}>
          {content}
        </h1>
      );
  }
};
